/* MASTER FEATURE LIST & COMPONENT REQUIREMENTS:
1.  Single-File Architecture: Entire application logic contained within App.js.
2.  Original Branding: Logo with orange-600 background and text-shadow: '2px 2px 6px rgba(0, 0, 0, 0.9)'.
3.  User Box Button Appearance: Toggle in header maintains a robust button appearance (bg-orange-700).
4.  Copyright Footer: Persistent gray-800 footer with dynamic year.
5.  Magic Link Authentication: Dual-box layout (Login/Register) using Firebase Magic Links.
6.  Profile Completion Guard: Mandatory Player ID and Birth Year check; redirects users until complete.
7.  Unified View Logic: Centralized navigation state (view/selectedId).
8.  Admin Suite: Exclusive access for admin IDs (aJZHuMydFRaG3hJBkcUFZMYF6Xr2).
9.  Admin User Management: Header button "Admin Users" to view profile details and delete users.
10. Tournament Types: Dropdown including Pokemon (Standard, GLC, Other, Live), MTG, and One Piece.
11. Dynamic Location Logic: If "Pokemon TCG Live - Online" is selected, hide Location field.
12. Categorized & Sorted Event Lists: Events sorted earliest to latest. "Active Events" vs. "Upcoming Events".
13. Registration Full Status: Automatically switches to "Registration Full" at capacity.
14. Registration Lifecycle: Admin can "Close Registration" manually or "Reopen Registration".
15. Unregister vs. Drop: Players "Unregister" before start. Once started, becomes "Drop from Tournament".
16. Admin Participant Control: Admin can unregister, drop, or DQ any participant.
17. DQ Logic: DQ'ed players moved to bottom of standings; impact tiebreakers.
18. Decklist Management: Submit/Edit/View for players; Admin can view all decklists.
19. Decklist Locking: Once the tournament starts, decklists are locked with no further ability to edit.
20. Date/Time Formatting: Always "Tuesday, December 31, 2025 - 6:30 PM EST".
21. 15-Minute Time Selection: Improved Admin UI with dropdowns for Hour, 00/15/30/45 Minutes, and AM/PM.
22. Tournament Description: Freeform description field displayed on the Details page.
23. Admin Panel Box: Dedicated box for Start Round, Add User (Testing), and Close Registration.
24. Dynamic Round Structure: Sets Swiss rounds and Top Cut based on TCG Single Day tables.
25. Swiss Pairing Engine: Prevents duplicate matchups across rounds.
26. Pairing UI: In-person events show Table Numbers; Online shows Opponent Username in parens.
27. Player Pairing Status Box: Top UI for players to see Round #, Opponent, and report results.
28. Standings Logic: Sorted by Points (3/Win, 1/Draw), then OWP, then OOWP. Byes = 3 Points.
29. Results Tab: Post-start tab showing matchups and results for ALL rounds.
30. Complete Tournament Button: Admin button appears in Control Center after final round results reported.
31. Delete Tournament Button: Admin button next to "Edit" to permanently remove event.
32. Edit Profile Title: Profile edit page is explicitly titled "Edit Profile".
33. Duplicate App Guard: Logic to prevent Firebase initialization errors during React HMR.
34. REQUIRED FEATURE: Indicate on Tournament Details if a Decklist is required.
35. REQUIRED FEATURE: Display Tournament Description on the Details page.
36. REQUIRED FEATURE: Footer text updated to "Copyright @ (Current Year)- Professor Maclid".
37. REQUIRED FEATURE: Capitalize Info, Standings, Pairings, and Results tabs on Details page.
38. REQUIRED FEATURE: Unauthenticated login button updated to "Login / Register".
39. REQUIRED FEATURE: Completed Tournaments section added; event headers made more prominent.
40. REQUIRED FEATURE: Sound alert plays whenever a new round starts.
41. REQUIRED FEATURE: Result Confirmation Workflow (Report -> Confirm/Dispute) with Admin Dispute Alerts.
42. REQUIRED FEATURE: Spell out "Best of 1" or "Best of 3" in all instances.
43. REQUIRED FEATURE: Synchronous Visual Countdown Timer (Aligned to Firebase Server Time Truth).
44. REQUIRED FEATURE: Alarm buzzer sounds when the countdown clock ends.
45. REQUIRED FEATURE: Admin can add a custom text label to the countdown clock.
46. REQUIRED FEATURE: Restore Admin ability to manually add test users to a tournament roster.
47. REQUIRED FEATURE: QR Code on Details page that returns the URL for that specific tournament.
48. REQUIRED FEATURE: Add visible text labels for all input fields on the Edit Profile page.
49. REQUIRED FEATURE: Admin bypass for visibility: Admin can always see participants/matchups without being registered.
50. REQUIRED FEATURE: Unauthenticated CTA Box: Display registration prompt for logged-out users above tournament list.
51. REQUIRED FEATURE: Persistent Session: Implement browserLocalPersistence for zero-timeout logins.
52. REQUIRED FEATURE: Admin Drop Capability: Global admin can drop players during registration or after start.
53. REQUIRED FEATURE: Admin DQ Capability: Global admin can disqualify players at any time.
54. REQUIRED FEATURE: Admin Decklist Visibility: Global admin can view any player's decklist in the Admin Panel.
55. REQUIRED FEATURE: Spam Folder Reminder: Notify users to check spam when Magic Link is sent.
56. REQUIRED FEATURE: Card Styling: Player count and Format must match Date/Time styling; Status must be prominent.
57. REQUIRED FEATURE: Admin User Details: Add a "Details" button to view full user JSON in Admin Panel.
58. REQUIRED FEATURE: Details Page Counts: Explicitly show Registered/Capacity on Tournament Details page.
59. REQUIRED FEATURE: Double Game Loss: Admin can assign a double loss (0 pts both).
60. REQUIRED FEATURE: Complete Tournament: Explicit action to finalize event when all rounds are done.
61. REQUIRED FEATURE: Match Chat & Judge Call: Real-time chat per match with profanity/"address" filter and Judge Alert.
62. REQUIRED FEATURE: No Chat for BYE: Chat interface hidden/disabled if player has a BYE.
63. REQUIRED FEATURE: Chat Lock: Admin can lock/unlock chat per table.
64. REQUIRED FEATURE: Action Logs: System logs in chat for Result Report, Confirmation, and Judge Updates.
65. REQUIRED FEATURE: Chat Visibility Fix: Chat input text must be dark (visible) against white background.
66. REQUIRED FEATURE: Explicit Result Visibility: Opponent sees exactly what was reported next to Confirm button.
67. REQUIRED FEATURE: Timestamps: Date/Time stamp on all chat logs and Match Creation.
68. REQUIRED FEATURE: Distinct Judge Notification: High visibility alert for Judge Calls in Chat and Admin view.
69. REQUIRED FEATURE: Roster Search: Admin can filter roster by partial match.
70. REQUIRED FEATURE: Audible Judge Alert: Distinct sound plays when a Judge is called.
71. REQUIRED FEATURE: Attendance Check (Live Events): 1-min countdown, player confirmation, visual status, pair gating.
72. REQUIRED FEATURE: Add to Calendar: Button in Details page to add event to Google Calendar.
73. REQUIRED FEATURE: Consistent Attendance Color: Attendance Check banner must match Round Notification (Orange).
74. REQUIRED FEATURE: Attendance Sound: Play audible sound when Attendance Check begins.
75. REQUIRED FEATURE: Immediate Pairing: Admin can pair Round 1 anytime during attendance check (no timer wait).
76. REQUIRED FEATURE: Admin Global Broadcast: Broadcast text message to all participants in Round Info box.
77. REQUIRED FEATURE: Default Date: Creating new tournament defaults to current date.
78. REQUIRED FEATURE: Completed Status: Completed events display "Completed" status.
79. REQUIRED FEATURE: Spam Text Update: Explicitly mention checking Junk/Spam folder.
80. REQUIRED FEATURE: Calendar Layout: Move Google button to header left of QR; Add Apple/Outlook (.ics) button.
81. REQUIRED FEATURE: Broadcast Sound: Sound notification when admin message is broadcast.
82. REQUIRED FEATURE: Static Broadcast UI: No flashing animation; Label changed to "Announcement:".
83. REQUIRED FEATURE: Calendar Icons/Text: "Add to..." text with calendar icons.
84. REQUIRED FEATURE: Scoped Judge Alerts: Audio/Visual alerts only for Admin and match participants.
85. REQUIRED FEATURE: Edit Profile UI: "Name" label, Box grouping for PID/Birth (Optional), Box for PTCG Live, "Save Profile" button.
86. REQUIRED FEATURE: Top Cut Logic: Ensure Top Cut rounds fire for Swiss with Top Cut (Top 4 for 9-20, Top 8 for 21+).
87. REQUIRED FEATURE: Live Handle in Pairings: Show opponent's PTCG Live handle in the pairing box for Live events (Inline/Consistent).
88. REQUIRED FEATURE: Live Handle in Lists: Show handles on Standings, Pairings list, and Results pages for Live events.
89. REQUIRED FEATURE: Auto-Forfeit on Drop: If a player is dropped, auto-resolve their current match as a loss.
90. REQUIRED FEATURE: Round Status Bar: Explicitly show pending matches instead of hiding Next Round button.
91. REQUIRED FEATURE: Empty Round Fail-Safe: "Regenerate Pairings" button if round exists but has 0 matches.
*/

import React, { useState, useEffect, useRef, useContext } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
    getAuth, onAuthStateChanged, isSignInWithEmailLink, 
    signInWithEmailLink, sendSignInLinkToEmail, signOut,
    setPersistence, browserLocalPersistence 
} from 'firebase/auth';
import { 
    getFirestore, doc, getDoc, setDoc, updateDoc, deleteDoc,
    collection, onSnapshot, query, orderBy, arrayUnion, arrayRemove, addDoc, serverTimestamp
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyDj5t3R1dKAzIobJ7fUYfu93vYzltg3FTc",
  authDomain: "professor-maclid.firebaseapp.com",
  projectId: "professor-maclid",
  storageBucket: "professor-maclid.firebasestorage.app",
  appId: "1:942037426113:web:f62ee02b54b5b675ba5940",
  measurementId: "G-YY3FPW08BF"
};

const firebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(firebaseApp);

setPersistence(auth, browserLocalPersistence);

const db = getFirestore(firebaseApp);
const appId = firebaseConfig.appId;
const adminUserIds = ['aJZHuMydFRaG3hJBkcUFZMYF6Xr2'];

const FirebaseContext = React.createContext(null);

// --- LOGIC HELPERS ---

const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "TBD";
    const [h, m] = timeStr.split(':');
    const date = new Date(`${dateStr}T${h}:${m}:00`);
    return new Intl.DateTimeFormat('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(date);
};

const formatChatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const CountdownTimer = ({ endTime, serverOffset, label }) => {
    const [timeLeft, setTimeLeft] = useState("");
    const buzzerPlayed = useRef(false);
    const alarmRef = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/951/951-preview.mp3"));

    useEffect(() => {
        if (!endTime) {
            buzzerPlayed.current = false;
            return;
        }
        const interval = setInterval(() => {
            const now = Date.now() + serverOffset;
            const distance = endTime - now;

            if (distance <= 0) {
                setTimeLeft("00:00");
                if (!buzzerPlayed.current) {
                    alarmRef.current.play().catch(e => console.log("Audio play blocked"));
                    buzzerPlayed.current = true;
                }
                clearInterval(interval);
            } else {
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);
                setTimeLeft(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [endTime, serverOffset]);

    if (!endTime) return null;
    return (
        <div className="flex flex-col items-center gap-1">
            {label && <span className="text-[10px] font-black uppercase text-gray-400 tracking-[0.2em]">{label}</span>}
            <div className="bg-black text-orange-500 px-6 py-2 rounded-full font-mono font-black text-2xl shadow-inner border-2 border-orange-900">
                {timeLeft}
            </div>
        </div>
    );
};

// Updated Logic based on chart
const calcStructure = (count) => {
    if (count >= 410) return { swiss: 10, cut: 8 };
    if (count >= 227) return { swiss: 9, cut: 8 };
    if (count >= 129) return { swiss: 8, cut: 8 };
    if (count >= 65) return { swiss: 7, cut: 8 };
    if (count >= 33) return { swiss: 6, cut: 8 };
    if (count >= 21) return { swiss: 5, cut: 8 }; // 21+ gets Top 8
    if (count >= 13) return { swiss: 5, cut: 4 }; // 13-20 gets Top 4
    if (count >= 9) return { swiss: 4, cut: 4 };  // 9-12 gets Top 4
    return { swiss: 3, cut: 0 }; // 4-8 gets 0 cut
};

const calcStandings = (players, rounds) => {
    if (!players || !rounds) return [];
    let stats = players.map(p => ({ ...p, points: 0, wins: 0, losses: 0, draws: 0, playedAgainst: new Set(), opps: [], winPct: 0 }));
    Object.values(rounds).forEach(round => {
        round.forEach(m => {
            const p1 = stats.find(s => s.userId === m.p1);
            const p2 = stats.find(s => s.userId === m.p2);
            if(p1 && m.p2 !== 'BYE') p1.playedAgainst.add(m.p2);
            if(p2 && m.p1 !== 'BYE') p2.playedAgainst.add(m.p1);
            if (!p1 || m.result === 'pending') return;
            if (m.p2 === 'BYE') { p1.points += 3; p1.wins++; return; } 
            if (!p2) return;
            p1.opps.push(m.p2); p2.opps.push(m.p1);
            if (m.result === 'p1') { p1.points += 3; p1.wins++; p2.losses++; }
            else if (m.result === 'p2') { p2.points += 3; p2.wins++; p1.losses++; }
            else if (m.result === 'draw') { p1.points += 1; p2.points += 1; p1.draws++; p2.draws++; }
            else if (m.result === 'doubleLoss') { p1.losses++; p2.losses++; } 
        });
    });
    stats.forEach(s => s.winPct = Math.max(s.points / ((s.wins + s.losses + s.draws || 1) * 3), 0.33));
    stats.forEach(s => s.owp = s.opps.length ? s.opps.reduce((acc, id) => acc + (stats.find(o => o.userId === id)?.winPct || 0.33), 0) / s.opps.length : 0.33);
    stats.forEach(s => s.oowp = s.opps.length ? s.opps.reduce((acc, id) => acc + (stats.find(o => o.userId === id)?.owp || 0.33), 0) / s.opps.length : 0.33);
    return stats.sort((a, b) => (a.dq !== b.dq) ? (a.dq ? 1 : -1) : (b.points - a.points || b.owp - a.owp || b.oowp - a.oowp));
};

const pairSwiss = (players, history) => {
    let ranked = calcStandings(players, history).filter(p => !p.dropped && !p.dq);
    let pairs = [];
    let pool = [...ranked];
    while (pool.length > 0) {
        const p1 = pool.shift();
        if (pool.length === 0) { 
            pairs.push({ table: pairs.length + 1, p1: p1.userId, p1Name: p1.name, p2: 'BYE', p2Name: 'BYE', result: 'p1', confirmStatus: 'confirmed' }); 
            break; 
        }
        let oppIdx = pool.findIndex(o => !p1.playedAgainst.has(o.userId));
        const p2 = oppIdx !== -1 ? pool.splice(oppIdx, 1)[0] : pool.shift();
        pairs.push({ 
            table: pairs.length + 1, p1: p1.userId, p1Name: p1.name, p1User: p1.ptcgLiveUsername || '', 
            p2: p2.userId, p2Name: p2.name, p2User: p2.ptcgLiveUsername || '', 
            result: 'pending', confirmStatus: 'none', reportedBy: null, 
            chat: [{ senderId: 'SYSTEM', senderName: 'SYSTEM', text: 'Match Created', timestamp: Date.now(), isSystem: true }], 
            judgeCallActive: false, chatLocked: false
        });
    }
    return pairs;
};

// FEATURE 86: Top Cut Logic
const pairTopCut = (players, history, currentRound, cutSize, totalSwiss) => {
    const createCutMatch = (p1, p2, label) => ({
        table: label, 
        p1: p1.userId, p1Name: p1.name, p1User: p1.ptcgLiveUsername || '', 
        p2: p2.userId, p2Name: p2.name, p2User: p2.ptcgLiveUsername || '', 
        result: 'pending', confirmStatus: 'none', reportedBy: null,
        chat: [{ senderId: 'SYSTEM', senderName: 'SYSTEM', text: 'Top Cut Match Created', timestamp: Date.now(), isSystem: true }],
        judgeCallActive: false, chatLocked: false
    });

    // If this is the first round of Top Cut
    if (currentRound === totalSwiss + 1) {
        const standings = calcStandings(players, history);
        const top = standings.filter(p => !p.dq).slice(0, cutSize);
        let pairs = [];

        if (cutSize === 8) {
            // Standard Top 8 Seeds: 1v8, 4v5, 2v7, 3v6
            pairs.push(createCutMatch(top[0], top[7], 'QF1'));
            pairs.push(createCutMatch(top[3], top[4], 'QF2'));
            pairs.push(createCutMatch(top[1], top[6], 'QF3'));
            pairs.push(createCutMatch(top[2], top[5], 'QF4'));
        } else if (cutSize === 4) {
             // Standard Top 4 Seeds: 1v4, 2v3
            pairs.push(createCutMatch(top[0], top[3], 'SF1'));
            pairs.push(createCutMatch(top[1], top[2], 'SF2'));
        }
        return pairs;
    }

    // Subsequent rounds (Semis or Finals)
    // We assume the bracket structure: QF1 plays QF2, QF3 plays QF4
    const prevRound = history[`round${currentRound - 1}`];
    // Find winners from previous round
    const winners = prevRound.map(m => {
        const uid = m.result === 'p1' ? m.p1 : m.p2;
        return players.find(p => p.userId === uid);
    });

    let pairs = [];
    // If we had 4 winners (QF), we pair them into 2 Semis
    // Based on order pushed above: Winner(QF1) vs Winner(QF2), Winner(QF3) vs Winner(QF4)
    if (winners.length === 4) {
         pairs.push(createCutMatch(winners[0], winners[1], 'SF1'));
         pairs.push(createCutMatch(winners[2], winners[3], 'SF2'));
    } 
    // If we had 2 winners (SF), we pair into Final
    else if (winners.length === 2) {
         pairs.push(createCutMatch(winners[0], winners[1], 'FINALS'));
    }

    return pairs;
};

// --- SUB-COMPONENTS ---

const MatchChat = ({ match, onSend, onCallJudge, onClearJudge, isJudge, userId }) => {
    const [msg, setMsg] = useState("");
    const restrictedWords = ["address", "shit", "fuck", "damn", "bitch", "ass", "cunt", "dick"]; // Basic filter
    const scrollRef = useRef(null);

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [match.chat]);

    if (match.p2 === 'BYE') return (
        <div className="bg-gray-100 rounded-xl border border-gray-200 h-24 flex items-center justify-center shadow-inner mt-4">
            <span className="text-gray-400 font-black uppercase text-xs tracking-widest">Chat Disabled (BYE)</span>
        </div>
    );

    const handleSend = (e) => {
        e.preventDefault();
        if (!msg.trim()) return;
        if (restrictedWords.some(w => msg.toLowerCase().includes(w))) {
            alert("Message contains restricted words and cannot be sent.");
            return;
        }
        onSend(msg);
        setMsg("");
    };

    const isLocked = match.chatLocked && !isJudge;

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-64 shadow-inner mt-4 relative">
            {match.judgeCallActive && (
                <div className="bg-red-600 text-white font-black text-center p-2 uppercase text-xs animate-pulse tracking-widest sticky top-0 z-10">
                    ⚠ JUDGE CALLED ⚠
                </div>
            )}
            <div className="bg-gray-100 px-4 py-2 border-b flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Match Chat</span>
                    {match.chatLocked && <span className="text-[8px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold uppercase border border-red-200">Locked</span>}
                </div>
                {isJudge && match.judgeCallActive && (
                    <button onClick={onClearJudge} className="bg-green-600 text-white px-2 py-1 rounded text-[8px] font-black uppercase">Resolve</button>
                )}
                {!isJudge && (
                    <button onClick={onCallJudge} disabled={match.judgeCallActive} className={`${match.judgeCallActive ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} text-white px-2 py-1 rounded text-[8px] font-black uppercase transition`}>
                        {match.judgeCallActive ? 'Judge Called' : 'Call Judge'}
                    </button>
                )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {match.chat?.map((c, i) => (
                    <div key={i} className={`flex flex-col ${c.isSystem ? 'items-center' : (c.senderId === userId ? 'items-end' : 'items-start')}`}>
                        <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs shadow-sm ${
                            c.isSystem ? 'bg-red-100 text-red-600 font-black border border-red-200 uppercase tracking-wide' : 
                            (c.senderId === userId ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800')
                        }`}>
                            {!c.isSystem && <span className="block text-[8px] font-bold opacity-75 mb-1">{c.senderName}</span>}
                            {c.text}
                        </div>
                        <span className="text-[8px] text-gray-400 mt-1 font-bold">{formatChatTime(c.timestamp)}</span>
                    </div>
                ))}
                {(!match.chat || match.chat.length === 0) && <p className="text-center text-[10px] text-gray-400 italic mt-10">No messages yet.</p>}
            </div>
            <form onSubmit={handleSend} className="p-2 border-t bg-white flex gap-2 relative z-10">
                <input disabled={isLocked} value={msg} onChange={e => setMsg(e.target.value)} placeholder={isLocked ? "Chat is locked by Admin" : "Type a message..."} className="flex-1 text-xs border rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 text-gray-900 disabled:bg-gray-100 disabled:text-gray-400" />
                <button disabled={isLocked} type="submit" className="bg-orange-600 text-white px-4 rounded-lg font-black text-[10px] uppercase disabled:bg-gray-400">Send</button>
            </form>
        </div>
    );
};

const Authentication = () => {
    const [loginEmail, setLoginEmail] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [sent, setSent] = useState(false);
    const handleAuth = async (e, type) => {
        e.preventDefault();
        const email = type === 'login' ? loginEmail : regEmail;
        try {
            if (type === 'reg') window.localStorage.setItem('pendingRegistration', JSON.stringify({ name: fullName }));
            await sendSignInLinkToEmail(auth, email, { url: window.location.href, handleCodeInApp: true });
            window.localStorage.setItem('emailForSignIn', email);
            setSent(true);
        } catch (err) { alert(err.message); }
    };
    if (sent) return <div className="p-10 text-center bg-white rounded-xl shadow-lg border mt-10 font-bold">Magic link sent! Check your inbox (and verify your Junk/Spam folder!).</div>;
    return (
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 p-4 font-sans mt-6">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100"><h2 className="text-2xl font-bold text-indigo-900 mb-6 uppercase tracking-tighter">Login</h2><form onSubmit={(e) => handleAuth(e, 'login')} className="space-y-4"><label className="block text-sm font-bold text-gray-700">Email Address</label><input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="w-full p-4 bg-gray-50 border-none rounded-2xl" required /><button className="w-full py-4 bg-indigo-600 text-white font-bold rounded-2xl uppercase shadow hover:bg-indigo-700 transition">Send Link</button></form></div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100"><h2 className="text-2xl font-bold text-emerald-900 mb-6 uppercase tracking-tighter">Register</h2><form onSubmit={(e) => handleAuth(e, 'reg')} className="space-y-4"><label className="block text-sm font-bold">Full Name</label><input value={fullName} onChange={e => setFullName(e.target.value)} className="w-full p-4 bg-gray-50 border-none rounded-2xl" required /><label className="block text-sm font-bold">Email Address</label><input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} className="w-full p-4 bg-gray-50 border-none rounded-2xl" required /><button className="w-full py-4 bg-emerald-600 text-white font-bold rounded-2xl uppercase shadow hover:bg-emerald-700 transition">Create Account</button></form></div>
        </div>
    );
};

const AdminUsersPanel = () => {
    const [users, setUsers] = useState([]);
    useEffect(() => onSnapshot(collection(db, `artifacts/${appId}/public/data/playerProfiles`), (s) => setUsers(s.docs.map(d => ({id: d.id, ...d.data()})))), []);
    const delUser = async (uid) => { if (window.confirm("Delete profile?")) await deleteDoc(doc(db, `artifacts/${appId}/public/data/playerProfiles`, uid)); };
    return (
        <div className="max-w-6xl mx-auto space-y-4 font-sans p-4">
            <h2 className="text-3xl font-black uppercase tracking-tighter text-gray-800 border-l-8 border-orange-600 pl-4">User Hub</h2>
            <div className="grid gap-4 md:grid-cols-2">
                {users.map(u => (
                    <div key={u.id} className="bg-white p-6 rounded-3xl border flex justify-between items-center shadow-sm">
                        <div><p className="font-bold text-xl">{u.name}</p><p className="text-xs text-gray-400">{u.email} | PID: {u.playerIdentifier}</p></div>
                        <div className="flex gap-2">
                            <button onClick={() => alert(JSON.stringify(u, null, 2))} className="bg-blue-50 text-blue-600 px-4 py-2 rounded-2xl font-black uppercase text-[10px] border border-blue-100 hover:bg-blue-100">Details</button>
                            <button onClick={() => delUser(u.id)} className="bg-red-50 text-red-600 px-4 py-2 rounded-2xl font-black uppercase text-[10px] border border-red-100 hover:bg-red-100">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const EventCard = ({ t, onClick, currentUser, active = false }) => (
    <div onClick={onClick} className={`group bg-white rounded-[2rem] shadow-sm border overflow-hidden cursor-pointer hover:shadow-2xl hover:-translate-y-1 transition-all duration-300`}>
        <div className={`h-4 w-full ${active ? 'bg-blue-600' : (t.status === 'completed' ? 'bg-gray-400' : 'bg-orange-600')}`} />
        <div className="p-8">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="font-black text-3xl uppercase tracking-tighter text-gray-900 group-hover:text-orange-600 transition-colors">{t.name}</h3>
                    <p className="font-bold text-orange-600 uppercase text-xs mt-1 tracking-widest">{t.type}</p>
                </div>
                {t.players?.some(p => p.userId === currentUser?.uid) && <span className="bg-green-100 text-green-700 text-[10px] px-3 py-1 rounded-full font-black uppercase border border-green-200">Registered</span>}
            </div>
            
            <div className="space-y-3 mt-6">
                <div className="flex items-center gap-3 text-gray-700">
                    <div className="bg-gray-100 p-2 rounded-lg">📅</div>
                    <p className="font-bold text-sm">{formatDateTime(t.date, t.time)}</p>
                </div>
                {t.type !== 'Pokemon TCG Live - Online' && (
                    <div className="flex items-center gap-3 text-gray-500">
                        <div className="bg-gray-100 p-2 rounded-lg">📍</div>
                        <p className="text-xs font-black uppercase">{t.location}</p>
                    </div>
                )}
                <div className="flex items-center gap-3 text-gray-700">
                    <div className="bg-gray-100 p-2 rounded-lg">👤</div>
                    <p className="font-bold text-sm">{t.players?.length || 0} / {t.maxParticipants} Players</p>
                </div>
                 <div className="flex items-center gap-3 text-gray-700">
                    <div className="bg-gray-100 p-2 rounded-lg">🏆</div>
                    <p className="font-bold text-sm">{t.structure}</p>
                </div>
            </div>

            <div className="mt-8 border-t pt-6 flex justify-start">
                <span className={`px-4 py-2 rounded-full font-black uppercase text-xs tracking-widest shadow-sm border ${
                    t.status === 'inProgress' ? 'bg-blue-100 text-blue-700 border-blue-200' : 
                    t.status === 'completed' ? 'bg-gray-100 text-gray-600 border-gray-200' : 
                    'bg-green-100 text-green-700 border-green-200'
                }`}>
                    {t.status === 'completed' ? 'Completed' : t.status.replace(/([A-Z])/g, ' $1')}
                </span>
            </div>
        </div>
    </div>
);

const TournamentList = ({ tournaments, navigateTo, isAdmin, currentUser }) => {
    const sorted = [...tournaments].sort((a,b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
    const active = sorted.filter(t => t.status === 'inProgress' || t.status === 'attendanceCheck');
    const upcoming = sorted.filter(t => t.status.startsWith('registration'));
    const completed = sorted.filter(t => t.status === 'completed');
    
    return (
        <div className="space-y-12 font-sans">
            {!currentUser && (
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-10 rounded-[2.5rem] shadow-2xl text-white text-center">
                    <h2 className="text-3xl font-black uppercase tracking-tighter mb-4">Ready to Play?</h2>
                    <p className="text-orange-100 font-bold mb-8 text-lg">Log in or Signup to Register and Play in our upcoming events!</p>
                    <button onClick={() => navigateTo('auth')} className="bg-white text-orange-600 px-10 py-4 rounded-2xl font-black uppercase text-sm shadow-xl hover:scale-105 transition transform">Get Started Now</button>
                </div>
            )}

            <div className="flex justify-between items-center">
                <h2 className="text-4xl font-black uppercase tracking-tighter text-gray-800">Events</h2>
                {isAdmin && <button onClick={() => navigateTo('createTournament')} className="bg-green-600 text-white px-6 py-3 rounded-xl font-black shadow-lg uppercase text-xs hover:bg-green-700 transition">+ New Event</button>}
            </div>

            <section className="space-y-6">
                <h3 className="font-black text-2xl uppercase tracking-widest text-gray-900 border-l-[12px] border-blue-500 px-2 pl-4">Active Events</h3>
                <div className="grid gap-8 md:grid-cols-2">
                    {active.map(t => <EventCard key={t.id} t={t} onClick={() => navigateTo('tournamentDetails', t.id)} currentUser={currentUser} active />)}
                    {active.length === 0 && <p className="text-gray-400 italic text-sm pl-2">No events currently underway.</p>}
                </div>
            </section>

            <section className="space-y-6">
                <h3 className="font-black text-2xl uppercase tracking-widest text-gray-900 border-l-[12px] border-orange-500 px-2 pl-4">Upcoming Events</h3>
                <div className="grid gap-8 md:grid-cols-2">
                    {upcoming.map(t => <EventCard key={t.id} t={t} onClick={() => navigateTo('tournamentDetails', t.id)} currentUser={currentUser} />)}
                    {upcoming.length === 0 && <p className="text-gray-400 italic text-sm pl-2">No upcoming events scheduled.</p>}
                </div>
            </section>

            <section className="space-y-6">
                <h3 className="font-black text-2xl uppercase tracking-widest text-gray-900 border-l-[12px] border-gray-400 px-2 pl-4">Completed Events</h3>
                <div className="grid gap-8 md:grid-cols-2">
                    {completed.map(t => <EventCard key={t.id} t={t} onClick={() => navigateTo('tournamentDetails', t.id)} currentUser={currentUser} />)}
                </div>
            </section>
        </div>
    );
};

const TournamentDetails = ({ tournamentId, navigateTo }) => {
    const { currentUser, pokemonPlayerProfile, isAdmin, isProfileComplete, serverOffset } = useContext(FirebaseContext);
    const [t, setT] = useState(null);
    const [allProfiles, setAllProfiles] = useState([]);
    const [standings, setStandings] = useState([]);
    const [activeTab, setActiveTab] = useState('INFO');
    const [deckView, setDeckView] = useState(null);
    const [deckInput, setDeckInput] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [adminChatView, setAdminChatView] = useState(null); 
    const [rosterSearch, setRosterSearch] = useState(''); 
    
    const chimeRef = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"));
    const judgeSoundRef = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3")); 
    const broadcastSoundRef = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"));

    const isFullyAuth = currentUser && !currentUser.isAnonymous && isProfileComplete(pokemonPlayerProfile);

    // Use a Ref to track active judge calls to prevent spamming sound on every re-render
    const prevJudgeCalls = useRef(new Set());
    // Feature 81: Track broadcast state for sound
    const prevBroadcastActive = useRef(false);

    useEffect(() => {
        if (!t || !t.rounds || !t.currentRound) return;
        
        // --- Feature 81: Broadcast Sound ---
        if (t.broadcastActive && !prevBroadcastActive.current) {
             broadcastSoundRef.current.play().catch(() => {});
        }
        prevBroadcastActive.current = t.broadcastActive;
        
        // --- Feature 84: Scoped Judge Call Sound ---
        const round = t.rounds[`round${t.currentRound}`];
        if (!round) return;

        const currentActiveIndices = new Set();
        round.forEach((m, idx) => { 
            // Only alert if isAdmin OR if the current user is in this match
            const isRelevant = isAdmin || (currentUser && (m.p1 === currentUser.uid || m.p2 === currentUser.uid));
            if(m.judgeCallActive && isRelevant) currentActiveIndices.add(idx); 
        });

        let newAlert = false;
        for (let idx of currentActiveIndices) {
            if (!prevJudgeCalls.current.has(idx)) {
                newAlert = true;
                break;
            }
        }

        if (newAlert) {
            judgeSoundRef.current.play().catch(() => {});
        }

        prevJudgeCalls.current = currentActiveIndices;
    }, [t, isAdmin, currentUser]);

    useEffect(() => {
        const unsubT = onSnapshot(doc(db, `artifacts/${appId}/public/data/tournaments`, tournamentId), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (t && data.currentRound > t.currentRound) { chimeRef.current.play().catch(() => {}); }
                // REQUIRED FEATURE 74: Play sound on attendance start
                if (t && t.status !== 'attendanceCheck' && data.status === 'attendanceCheck') { chimeRef.current.play().catch(() => {}); }
                setT({ id: snap.id, ...data });
                setStandings(calcStandings(data.players || [], data.rounds || {}));
            }
        });
        const unsubU = onSnapshot(collection(db, `artifacts/${appId}/public/data/playerProfiles`), (snap) => {
            setAllProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => { unsubT(); unsubU(); };
    }, [tournamentId, t?.currentRound]);

    if (!t) return <div className="p-10 text-center font-black text-orange-600 uppercase">Loading Tournament Data...</div>;

    const playerEntry = t.players?.find(p => p.userId === currentUser?.uid);
    const matches = t.rounds?.[`round${t.currentRound}`] || [];
    const playerMatchIndex = matches.findIndex(m => m.p1 === currentUser?.uid || m.p2 === currentUser?.uid);
    const playerMatch = matches[playerMatchIndex];
    
    const allDone = matches.length > 0 && matches.every(m => m.confirmStatus === 'confirmed');
    const hasDisputes = matches.some(m => m.confirmStatus === 'disputed');

    const isAttendancePhase = t.status === 'attendanceCheck';
    const allPresent = t.players?.every(p => p.dropped || p.dq || p.present);
    const attendanceTimerFinished = t.attendanceTimerEnd && (Date.now() + serverOffset) > t.attendanceTimerEnd;
    const canPairRound1 = isAttendancePhase && (allPresent || attendanceTimerFinished);

    const shareUrl = `${window.location.origin}${window.location.pathname}?t=${t.id}`;
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareUrl)}`;

    const createGoogleCalendarUrl = (tournament) => {
        if (!tournament.date || !tournament.time) return '#';
        const [h, m] = tournament.time.split(':');
        const startDate = new Date(`${tournament.date}T${h}:${m}:00`);
        const endDate = new Date(startDate.getTime() + (4 * 60 * 60 * 1000)); // Assume 4 hours
        
        const formatGCal = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, "");
        
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(tournament.name)}&dates=${formatGCal(startDate)}/${formatGCal(endDate)}&details=${encodeURIComponent(tournament.description || "")}&location=${encodeURIComponent(tournament.location || "Online")}`;
    };

    const downloadICS = (tournament) => {
        if (!tournament.date || !tournament.time) return;
        const [h, m] = tournament.time.split(':');
        const startDate = new Date(`${tournament.date}T${h}:${m}:00`);
        const endDate = new Date(startDate.getTime() + (4 * 60 * 60 * 1000));

        const formatICS = (date) => date.toISOString().replace(/-|:|\.\d+/g, "");
        
        const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Professor Maclid//EN\nBEGIN:VEVENT\nUID:${tournament.id}@profmaclid\nDTSTAMP:${formatICS(new Date())}\nDTSTART:${formatICS(startDate)}\nDTEND:${formatICS(endDate)}\nSUMMARY:${tournament.name}\nDESCRIPTION:${tournament.description || ""}\nLOCATION:${tournament.location || "Online"}\nEND:VEVENT\nEND:VCALENDAR`;

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `${tournament.name.replace(/\s+/g, '_')}.ics`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleReg = async (userObj = null) => {
        const profile = userObj || pokemonPlayerProfile;
        if (t.type.includes('Live') && !profile.ptcgLiveUsername) { alert("Username required."); return navigateTo('editProfile'); }
        const updated = [...(t.players || []), { userId: profile.userId || profile.id, name: profile.name, ptcgLiveUsername: profile.ptcgLiveUsername || '', decklist: '', dropped: false, dq: false, present: false }];
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { players: updated, status: updated.length >= t.maxParticipants ? 'registrationFull' : t.status });
    };

    // FEATURE 89: Updated Unreg/Drop Logic with Auto-Forfeit
    const handleUnregDrop = async (uid = currentUser.uid) => {
        if (t.status.startsWith('registration')) {
            const updated = t.players.filter(p => p.userId !== uid);
            await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { players: updated, status: (t.status === 'registrationFull' && updated.length < t.maxParticipants) ? 'registrationOpen' : t.status });
        } else {
            // Check for active match and auto-resolve it
            const roundKey = `round${t.currentRound}`;
            const currentMatches = t.rounds[roundKey] ? [...t.rounds[roundKey]] : [];
            const matchIdx = currentMatches.findIndex(m => m.p1 === uid || m.p2 === uid);
            
            let roundsUpdate = {};
            // If they are in a match that isn't confirmed yet, auto-forfeit them
            if (matchIdx !== -1 && currentMatches[matchIdx].confirmStatus !== 'confirmed') {
                const m = currentMatches[matchIdx];
                // If p1 dropped, p2 wins. If p2 dropped, p1 wins.
                const winner = m.p1 === uid ? 'p2' : 'p1'; 
                currentMatches[matchIdx] = {
                    ...m,
                    result: winner,
                    confirmStatus: 'confirmed',
                    chat: [...(m.chat || []), { senderId: 'SYSTEM', senderName: 'SYSTEM', text: 'Player Dropped - Auto Forfeit', timestamp: Date.now(), isSystem: true }]
                };
                roundsUpdate = { [`rounds.${roundKey}`]: currentMatches };
            }

            const updated = t.players.map(p => p.userId === uid ? { ...p, dropped: true } : p);
            await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { players: updated, ...roundsUpdate });
        }
    };

    const handleDQ = async (uid) => {
        if (!window.confirm("Disqualify this player? This cannot be undone.")) return;
        const updated = t.players.map(p => p.userId === uid ? { ...p, dq: true } : p);
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { players: updated });
    };

    const confirmPresence = async () => {
        if (!playerEntry) return;
        const updated = t.players.map(p => p.userId === currentUser.uid ? { ...p, present: true } : p);
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { players: updated });
    };

    const handleMatchRes = async (idx, res) => {
        const rounds = { ...t.rounds };
        rounds[`round${t.currentRound}`][idx].result = res;
        rounds[`round${t.currentRound}`][idx].reportedBy = currentUser.uid;
        rounds[`round${t.currentRound}`][idx].confirmStatus = 'reported';
        const chat = rounds[`round${t.currentRound}`][idx].chat || [];
        const resultText = res === 'p1' ? 'P1 Win' : res === 'p2' ? 'P2 Win' : 'Draw';
        chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `${pokemonPlayerProfile.name} reported result: ${resultText}`, timestamp: Date.now(), isSystem: true });
        rounds[`round${t.currentRound}`][idx].chat = chat;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds });
    };

    const handleConfirmation = async (idx, status) => {
        const rounds = { ...t.rounds };
        rounds[`round${t.currentRound}`][idx].confirmStatus = status;
        const chat = rounds[`round${t.currentRound}`][idx].chat || [];
        chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `Result ${status === 'confirmed' ? 'Verified' : 'DISPUTED'}`, timestamp: Date.now(), isSystem: true });
        rounds[`round${t.currentRound}`][idx].chat = chat;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds });
    };

    const updateChat = async (matchIdx, msgText, isSystem = false, isJudgeCall = false) => {
        const rounds = { ...t.rounds };
        const currentChat = rounds[`round${t.currentRound}`][matchIdx].chat || [];
        const msg = {
            senderId: isSystem ? 'SYSTEM' : currentUser.uid,
            senderName: isSystem ? 'SYSTEM' : pokemonPlayerProfile.name,
            text: msgText,
            timestamp: Date.now(),
            isSystem
        };
        rounds[`round${t.currentRound}`][matchIdx].chat = [...currentChat, msg];
        if (isJudgeCall) rounds[`round${t.currentRound}`][matchIdx].judgeCallActive = true;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds });
    };

    const clearJudgeCall = async (matchIdx) => {
        const rounds = { ...t.rounds };
        rounds[`round${t.currentRound}`][matchIdx].judgeCallActive = false;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds });
    };
    
    const toggleChatLock = async (matchIdx) => {
         const rounds = { ...t.rounds };
         const currentStatus = rounds[`round${t.currentRound}`][matchIdx].chatLocked || false;
         rounds[`round${t.currentRound}`][matchIdx].chatLocked = !currentStatus;
         await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds });
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6 font-sans">
             {/* REQUIRED FEATURE 76 & 82: Admin Global Broadcast Display - No flashing, Updated Label */}
             {t.broadcastMessage && t.broadcastActive && (
                <div className="bg-blue-600 text-white p-4 text-center font-bold uppercase tracking-widest rounded-xl shadow-lg border-2 border-white">
                    📢 Announcement: {t.broadcastMessage}
                </div>
            )}

            {isAttendancePhase && playerEntry && !playerEntry.present && (
                <div className="bg-orange-700 text-white p-8 rounded-[2rem] shadow-2xl text-center animate-pulse border-2 border-orange-500">
                    <h2 className="text-3xl font-black uppercase mb-4 tracking-tighter">Attendance Check!</h2>
                    <p className="mb-6 font-bold text-orange-100">Please confirm you are here to play Round 1.</p>
                    <CountdownTimer endTime={t.attendanceTimerEnd} serverOffset={serverOffset} />
                    <button onClick={confirmPresence} className="mt-6 bg-white text-orange-600 px-8 py-4 rounded-xl font-black uppercase shadow-xl hover:scale-105 transition">I'm Here!</button>
                </div>
            )}
            
            {isFullyAuth && playerMatch && t.status === 'inProgress' && !playerEntry?.dropped && !playerEntry?.dq && (
                <div className="bg-orange-700 text-white p-6 rounded-2xl shadow-xl border-2 border-orange-500">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="text-center md:text-left">
                            <p className="text-xs font-black uppercase opacity-70">Round {t.currentRound} Pairing</p>
                            {/* REQUIRED FEATURE 87: Show Opponent Handle for Live Events - Inline, Same Font */}
                            <h3 className="text-2xl font-black tracking-tight">
                                vs {playerMatch.p1 === currentUser.uid ? playerMatch.p2Name : playerMatch.p1Name}
                                {t.type.includes('Live') && ` (${playerMatch.p1 === currentUser.uid ? playerMatch.p2User : playerMatch.p1User})`}
                            </h3>
                            <p className="font-bold text-orange-200 uppercase text-sm">
                                {playerMatch.confirmStatus === 'reported' ? (
                                    playerMatch.reportedBy === currentUser.uid ? 
                                    "Waiting for Opponent..." : 
                                    `Opponent Reported: ${
                                        playerMatch.result === 'draw' ? "Draw" : 
                                        (playerMatch.result === 'p1' && playerMatch.p1 === playerMatch.reportedBy ? "They Won" : 
                                        (playerMatch.result === 'p2' && playerMatch.p2 === playerMatch.reportedBy ? "They Won" : "They Lost"))
                                    } - Confirm?`
                                ) : (playerMatch.confirmStatus === 'confirmed' ? "Result Verified" : "Action Required")}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {playerMatch.confirmStatus === 'none' && (
                                <>
                                    <button onClick={() => handleMatchRes(matches.indexOf(playerMatch), playerMatch.p1 === currentUser.uid ? 'p1' : 'p2')} className="bg-green-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">I Won</button>
                                    <button onClick={() => handleMatchRes(matches.indexOf(playerMatch), playerMatch.p1 === currentUser.uid ? 'p2' : 'p1')} className="bg-red-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">I Lost</button>
                                    <button onClick={() => handleMatchRes(matches.indexOf(playerMatch), 'draw')} className="bg-gray-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">Draw</button>
                                </>
                            )}
                            {playerMatch.confirmStatus === 'reported' && playerMatch.reportedBy !== currentUser.uid && (
                                <>
                                    <button onClick={() => handleConfirmation(matches.indexOf(playerMatch), 'confirmed')} className="bg-green-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">Confirm</button>
                                    <button onClick={() => handleConfirmation(matches.indexOf(playerMatch), 'disputed')} className="bg-red-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">Dispute</button>
                                </>
                            )}
                        </div>
                    </div>
                    <MatchChat 
                        match={playerMatch} 
                        onSend={(txt) => updateChat(playerMatchIndex, txt)} 
                        onCallJudge={() => updateChat(playerMatchIndex, "JUDGE CALLED!", true, true)}
                        isJudge={false}
                        userId={currentUser.uid}
                    />
                </div>
            )}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-lg border border-gray-100">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-6">
                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-4xl font-black uppercase tracking-tighter text-gray-900">{t.name}</h2>
                        {t.decklistRequired && <span className="inline-block mt-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-black text-[10px] uppercase">Decklist Required</span>}
                        {t.timerEnd && <div className="mt-4"><CountdownTimer endTime={t.timerEnd} serverOffset={serverOffset} label={t.timerLabel} /></div>}
                        {isAttendancePhase && <div className="mt-4"><CountdownTimer endTime={t.attendanceTimerEnd} serverOffset={serverOffset} label="Attendance Check" /></div>}
                    </div>
                    
                    {/* REQUIRED FEATURES 80 & 83: Calendar Buttons with Icons and Updated Text */}
                    <div className="flex flex-col gap-2">
                         <a href={createGoogleCalendarUrl(t)} target="_blank" rel="noopener noreferrer" className="bg-white text-gray-600 px-4 py-2 rounded-lg font-bold uppercase text-[10px] border border-gray-300 hover:bg-gray-50 shadow-sm text-center flex items-center justify-center gap-2">
                            <span>📅</span> Add to Google Calendar
                        </a>
                        <button onClick={() => downloadICS(t)} className="bg-white text-gray-600 px-4 py-2 rounded-lg font-bold uppercase text-[10px] border border-gray-300 hover:bg-gray-50 shadow-sm text-center flex items-center justify-center gap-2">
                            <span>📅</span> Add to Apple/Outlook Calendar
                        </button>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-3xl border border-gray-100 flex flex-col items-center shadow-inner">
                        <img src={qrImageUrl} alt="Tournament QR" className="w-24 h-24 md:w-32 md:h-32 rounded-xl mb-2" />
                        <span className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Scan to Share</span>
                    </div>

                    {isAdmin && (
                        <div className="flex gap-2">
                            <button onClick={() => navigateTo('editTournament', t.id)} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-xs shadow">EDIT</button>
                            <button onClick={async () => { if(window.confirm("Delete?")) { await deleteDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id)); navigateTo('tournamentList'); } }} className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-xs uppercase shadow">Delete</button>
                        </div>
                    )}
                </div>
                <div className="grid md:grid-cols-2 gap-8 border-t pt-6">
                    <div className="space-y-1">
                        <p className="font-bold text-orange-600 uppercase tracking-widest text-xs">{t.type}</p>
                        <p className="font-bold text-gray-700">{formatDateTime(t.date, t.time)}</p>
                    </div>
                    <div className="md:text-right">
                        <p className="font-bold uppercase text-gray-400 text-xs">{t.players?.length || 0} / {t.maxParticipants} Registered</p>
                        <p className="font-bold uppercase text-gray-400 text-xs">{t.structure} | {t.matchFormat} | {t.roundTimeLimit} minute rounds</p>
                        <p className="font-black text-blue-600 uppercase mt-1">{t.status.replace(/([A-Z])/g, ' $1')}</p>
                    </div>
                </div>
                {isAdmin && (
                    <div className="mt-8 p-6 bg-gray-900 text-white rounded-2xl border-4 border-orange-500 shadow-2xl space-y-4">
                        <h4 className="text-orange-500 font-black uppercase text-xs mb-4 tracking-widest text-center">Admin Control Panel</h4>
                        {hasDisputes && <div className="bg-red-600 text-white p-2 rounded text-center font-bold text-[10px] uppercase animate-pulse">Disputed Matches Found!</div>}
                        
                         {/* REQUIRED FEATURE 76: Admin Global Broadcast Input */}
                         <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 mb-4">
                             <h5 className="text-[10px] font-black uppercase text-gray-500 mb-2 tracking-[0.2em]">Global Broadcast</h5>
                             <div className="flex gap-2">
                                <input id="broadcastInput" type="text" className="flex-1 bg-gray-900 border border-gray-600 rounded p-2 font-bold text-xs text-white" placeholder="Message to all players..." defaultValue={t.broadcastMessage || ""} />
                                <button onClick={async () => {
                                     const msg = document.getElementById('broadcastInput').value;
                                     await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { broadcastMessage: msg, broadcastActive: true });
                                }} className="bg-blue-600 px-3 py-1 rounded font-black text-[10px] uppercase shadow">Broadcast</button>
                                <button onClick={async () => {
                                     await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { broadcastActive: false });
                                }} className={`px-3 py-1 rounded font-black text-[10px] uppercase shadow ${t.broadcastActive ? 'bg-red-600' : 'bg-gray-600'}`}>{t.broadcastActive ? 'Turn Off' : 'Off'}</button>
                             </div>
                        </div>

                        <div className="flex items-center justify-center gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700">
                             <div className="text-center">
                                <label className="block text-[8px] font-black uppercase text-gray-500 mb-1">Duration (Mins)</label>
                                <input id="timerMins" type="number" defaultValue={t.roundTimeLimit} className="w-16 bg-gray-900 border border-gray-600 rounded p-1 text-center font-black text-orange-500" />
                             </div>
                             <div className="text-left flex-1">
                                <label className="block text-[8px] font-black uppercase text-gray-500 mb-1">Timer Label</label>
                                <input id="timerLabelInput" type="text" className="w-full bg-gray-900 border border-gray-600 rounded p-1 font-bold text-xs text-white" placeholder="Label text..." defaultValue={t.timerLabel || ""} />
                             </div>
                             <button onClick={async () => {
                                 const mins = parseInt(document.getElementById('timerMins').value);
                                 const label = document.getElementById('timerLabelInput').value;
                                 const end = (Date.now() + serverOffset) + (mins * 60 * 1000);
                                 await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { timerEnd: end, timerLabel: label });
                             }} className="bg-orange-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">Start Timer</button>
                             <button onClick={async () => { await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { timerEnd: null, timerLabel: "" }); }} className="bg-gray-600 px-4 py-2 rounded-lg font-black text-xs uppercase shadow">Reset</button>
                        </div>
                        
                        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                            <h5 className="text-[10px] font-black uppercase text-gray-500 mb-4 tracking-[0.2em] border-b border-gray-700 pb-2">Manage Roster</h5>
                            <input 
                                type="text" 
                                placeholder="Search Player Name or ID..." 
                                className="w-full mb-3 p-2 bg-gray-900 border border-gray-600 rounded text-xs text-white placeholder-gray-500"
                                value={rosterSearch}
                                onChange={(e) => setRosterSearch(e.target.value)}
                            />
                            <div className="grid gap-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                {t.players?.filter(p => p.name.toLowerCase().includes(rosterSearch.toLowerCase()) || (p.ptcgLiveUsername && p.ptcgLiveUsername.toLowerCase().includes(rosterSearch.toLowerCase()))).map(p => (
                                    <div key={p.userId} className="flex items-center justify-between bg-gray-900 p-3 rounded-lg border border-gray-700 text-xs">
                                        <div className="flex-1 flex items-center gap-2">
                                            {isAttendancePhase && (
                                                <span title={p.present ? "Present" : "Absent"}>{p.present ? "✅" : "⏳"}</span>
                                            )}
                                            <div>
                                                <p className={`font-black uppercase ${p.dq ? 'text-red-500 line-through' : (p.dropped ? 'text-gray-500' : 'text-white')}`}>{p.name}</p>
                                                <p className="text-[8px] text-gray-500 font-bold uppercase">{p.dq ? 'DQ' : (p.dropped ? 'Dropped' : 'Active')}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {t.decklistRequired && (
                                                <button onClick={() => setDeckView(p)} className="bg-blue-600/20 text-blue-400 px-2 py-1 rounded text-[8px] font-black uppercase hover:bg-blue-600 hover:text-white transition">View List</button>
                                            )}
                                            {!p.dropped && !p.dq && (
                                                <button onClick={() => handleUnregDrop(p.userId)} className="bg-orange-600/20 text-orange-400 px-2 py-1 rounded text-[8px] font-black uppercase hover:bg-orange-600 hover:text-white transition">Drop</button>
                                            )}
                                            {!p.dq && (
                                                <button onClick={() => handleDQ(p.userId)} className="bg-red-600/20 text-red-400 px-2 py-1 rounded text-[8px] font-black uppercase hover:bg-red-600 hover:text-white transition">DQ</button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!t.players || t.players.length === 0) && <p className="text-gray-600 italic text-[10px] text-center py-4">No players registered.</p>}
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 justify-center">
                            <div className="flex items-center gap-2 bg-gray-800 p-2 rounded-xl border border-gray-700 shadow-inner">
                                <select id="admAddP" className="bg-transparent border-none text-[10px] font-black uppercase outline-none text-white cursor-pointer max-w-[150px]">
                                    <option value="" disabled className="text-black">Add Registered User...</option>
                                    {allProfiles.filter(u => !t.players?.some(p => p.userId === u.id)).map(u => <option key={u.id} value={u.id} className="text-black">{u.name}</option>)}
                                </select>
                                <button onClick={() => {
                                    const selId = document.getElementById('admAddP').value;
                                    if (selId) handleReg(allProfiles.find(u => u.id === selId));
                                }} className="bg-emerald-600 px-3 py-1 rounded font-bold text-[10px] uppercase shadow hover:bg-emerald-700">Add User</button>
                            </div>
                            
                            {t.status.startsWith('registration') && (
                                <button onClick={async () => {
                                    if (t.type.includes('Live')) {
                                        const end = (Date.now() + serverOffset) + (60 * 1000); 
                                        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { status: 'attendanceCheck', attendanceTimerEnd: end });
                                    } else {
                                        const { swiss, cut } = calcStructure(t.players?.filter(p => !p.dropped && !p.dq).length || 0);
                                        const pairs = pairSwiss(t.players, {});
                                        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { status: 'inProgress', totalSwissRounds: swiss, topCutSize: cut, currentRound: 1, [`rounds.round1`]: pairs });
                                    }
                                }} className="bg-green-600 px-6 py-2 rounded-xl font-bold uppercase text-sm shadow">Start Tournament</button>
                            )}

                            {/* REQUIRED FEATURE 75: Immediate Pairing Button (Attendance Phase) */}
                            {isAttendancePhase && (
                                <button onClick={async () => {
                                    const { swiss, cut } = calcStructure(t.players?.filter(p => !p.dropped && !p.dq).length || 0);
                                    const pairs = pairSwiss(t.players, {});
                                    await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { status: 'inProgress', totalSwissRounds: swiss, topCutSize: cut, currentRound: 1, [`rounds.round1`]: pairs, attendanceTimerEnd: null });
                                }} className="bg-green-600 px-6 py-2 rounded-xl font-bold uppercase text-sm shadow animate-bounce">Pair Round 1</button>
                            )}

                             {/* FEATURE 91: Fail-Safe for Empty Rounds */}
                            {t.status === 'inProgress' && matches.length === 0 && (
                                <div className="flex flex-col items-center gap-2 bg-red-900/20 p-2 rounded-xl border border-red-500/50">
                                    <p className="text-red-500 font-black text-[10px] uppercase tracking-widest">⚠ Error: Round {t.currentRound} is Empty</p>
                                    <button onClick={async () => {
                                         // 1. Recalculate structure (in case drops happened)
                                         const activePlayers = t.players?.filter(p => !p.dropped && !p.dq).length || 0;
                                         const { swiss, cut } = calcStructure(activePlayers);
                                         
                                         // 2. Generate Pairings based on current history
                                         const ps = pairSwiss(t.players, t.rounds || {});
                                         
                                         // 3. Save to Database (Force update current round)
                                         await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { 
                                             [`rounds.round${t.currentRound}`]: ps,
                                             totalSwissRounds: swiss, 
                                             topCutSize: cut 
                                         });
                                    }} className="bg-red-600 px-6 py-2 rounded-xl font-bold uppercase text-xs shadow-lg hover:bg-red-700 animate-pulse text-white">
                                        Regenerate Pairings
                                    </button>
                                </div>
                            )}

                             {/* FEATURE 90: Improved Next Round Button logic with Status Bar */}
                             {t.status === 'inProgress' && matches.length > 0 && t.currentRound < t.totalSwissRounds && (
                                allDone && !hasDisputes ? (
                                    <button onClick={async () => {
                                        const next = t.currentRound + 1;
                                        const ps = pairSwiss(t.players, t.rounds);
                                        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { currentRound: next, timerEnd: null, timerLabel: "", [`rounds.round${next}`]: ps });
                                    }} className="bg-indigo-600 px-6 py-2 rounded-xl font-bold uppercase text-xs shadow hover:bg-indigo-700 transition">Generate Round {t.currentRound + 1}</button>
                                ) : (
                                    <div className="bg-gray-800 text-gray-400 px-6 py-2 rounded-xl font-bold uppercase text-[10px] border border-gray-600 shadow-inner flex items-center gap-2 cursor-help" title="All matches must be confirmed to advance.">
                                        <span className="animate-pulse text-orange-500">●</span> 
                                        {hasDisputes 
                                            ? "CANNOT ADVANCE: Resolve Disputed Matches" 
                                            : `Waiting for ${matches.filter(m => m.confirmStatus !== 'confirmed').length} Active Match(es)`
                                        }
                                    </div>
                                )
                            )}

                            {/* Feature 86: Top Cut Logic - Start Cut or Next Bracket Round */}
                            {t.status === 'inProgress' && allDone && !hasDisputes && t.topCutSize > 0 && t.currentRound >= t.totalSwissRounds && (
                                <button onClick={async () => {
                                    const next = t.currentRound + 1;
                                    // Pair Top Cut Logic
                                    const ps = pairTopCut(t.players, t.rounds, next, t.topCutSize, t.totalSwissRounds);
                                    if (ps.length === 0) {
                                         // Finished brackets
                                         await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { status: 'completed', timerEnd: null });
                                    } else {
                                        await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { currentRound: next, timerEnd: null, timerLabel: "", [`rounds.round${next}`]: ps });
                                    }
                                }} className="bg-indigo-600 px-6 py-2 rounded-xl font-bold uppercase text-xs shadow">{t.currentRound === t.totalSwissRounds ? 'Start Top Cut' : 'Generate Bracket Round'}</button>
                            )}

                            {/* Complete Tournament - Only if No Cut, or Cut Finished */}
                            {t.status === 'inProgress' && allDone && !hasDisputes && ((t.topCutSize === 0 && t.currentRound === t.totalSwissRounds) || (t.topCutSize > 0 && t.rounds[`round${t.currentRound}`]?.length === 1)) && (
                                <button onClick={async () => {
                                    await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { status: 'completed', timerEnd: null });
                                }} className="bg-purple-600 px-6 py-2 rounded-xl font-bold uppercase text-xs shadow hover:bg-purple-700">Complete Tournament</button>
                            )}
                        </div>
                    </div>
                )}
                <div className="flex space-x-6 border-b my-6 overflow-x-auto no-scrollbar font-black uppercase text-xs">
                    {['INFO', ...(isAdmin || (t.status !== 'registrationOpen' && t.status !== 'registrationFull') ? ['PAIRINGS', 'RESULTS', 'STANDINGS'] : [])].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-4 tracking-widest transition-all ${activeTab === tab ? 'border-b-4 border-orange-600 text-orange-600' : 'text-gray-400 hover:text-gray-600'}`}>{tab}</button>
                    ))}
                </div>
                {activeTab === 'INFO' && (
                    <div className="space-y-6">
                        {t.description && (
                            <div className="p-6 bg-gray-50 border border-gray-100 rounded-xl">
                                <h4 className="font-black uppercase text-xs text-gray-400 mb-2">Tournament Notes</h4>
                                <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{t.description}</p>
                            </div>
                        )}
                        
                        {currentUser && !playerEntry && (t.status === 'registrationOpen' || t.status === 'registrationFull') && (
                            <button onClick={() => handleReg()} disabled={t.status === 'registrationFull'} className="w-full py-4 bg-orange-600 text-white font-black rounded-xl uppercase shadow hover:bg-orange-700 disabled:opacity-50">Register Now</button>
                        )}
                        {playerEntry && !playerEntry.dropped && !playerEntry.dq && (
                            <button onClick={() => handleUnregDrop()} className="w-full py-2 bg-red-50 text-red-600 font-bold rounded-lg uppercase text-xs border border-red-100 hover:bg-red-100 transition">{t.status.startsWith('registration') ? 'Unregister from Event' : 'Drop from Tournament'}</button>
                        )}
                        {playerEntry && t.decklistRequired && (
                            <div className="p-6 bg-blue-50 border border-blue-100 rounded-xl shadow-inner">
                                <h4 className="font-black text-blue-900 mb-4 uppercase text-xs tracking-widest">Decklist Submission</h4>
                                {t.status === 'inProgress' || t.status === 'completed' ? (
                                    <div className="space-y-3"><p className="text-xs font-black uppercase text-red-600 opacity-60">Locked</p><pre className="bg-white/50 p-4 text-xs border rounded-xl overflow-auto max-h-48 font-mono">{playerEntry.decklist || "No list provided."}</pre></div>
                                ) : (
                                    isEditing || !playerEntry.decklist ? (
                                        <div className="space-y-3"><textarea className="w-full p-3 border rounded-xl h-48 font-mono text-sm bg-white shadow-sm" value={deckInput || playerEntry.decklist} onChange={e => setDeckInput(e.target.value)} placeholder="Paste list..." /><button onClick={async () => { const up = t.players.map(p => p.userId === currentUser.uid ? {...p, decklist: deckInput} : p); await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), {players: up}); setIsEditing(false); }} className="bg-blue-600 text-white px-6 py-2 rounded font-bold shadow uppercase text-xs">Submit</button></div>
                                    ) : (
                                        <div><pre className="bg-white p-4 text-xs border rounded-xl mb-4 overflow-auto max-h-48 font-mono shadow-inner">{playerEntry.decklist}</pre><button onClick={() => { setIsEditing(true); setDeckInput(playerEntry.decklist); }} className="text-blue-600 font-bold underline text-xs uppercase">Edit Submission</button></div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'RESULTS' && (
                    <div className="space-y-8">
                        {Object.keys(t.rounds || {}).sort().reverse().map(rKey => (
                            <div key={rKey} className="space-y-2">
                                <h4 className="font-black uppercase text-xs tracking-widest text-gray-300 border-b pb-2">Round {rKey.replace('round', '')} History</h4>
                                <div className="grid gap-2">{t.rounds[rKey].map((m, idx) => (
                                        <div key={idx} className="flex justify-between items-center bg-gray-50 p-4 rounded-xl text-xs font-bold border border-white shadow-sm">
                                            <div className="w-1/3 text-right pr-4 truncate">
                                                {m.p1Name}
                                                {/* REQUIRED FEATURE 88: Show Live Handle in Results */}
                                                {t.type.includes('Live') && m.p1User ? ` (${m.p1User})` : ''}
                                            </div>
                                            <div className="bg-white px-4 py-1.5 rounded-full border text-orange-600 font-black">{m.result === 'pending' ? 'VS' : (m.result === 'p1' ? '1 - 0' : m.result === 'p2' ? '0 - 1' : m.result === 'doubleLoss' ? '0 - 0' : '0.5 - 0.5')}</div>
                                            <div className="w-1/3 text-left pl-4 truncate">
                                                {m.p2Name}
                                                {/* REQUIRED FEATURE 88: Show Live Handle in Results */}
                                                {t.type.includes('Live') && m.p2User ? ` (${m.p2User})` : ''}
                                            </div>
                                        </div>
                                ))}</div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'PAIRINGS' && (
                    <div className="grid gap-3">{matches.map((m, i) => (
                                <div key={i} className={`p-4 rounded-2xl border flex flex-col gap-4 ${m.confirmStatus === 'disputed' ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                        {t.type !== 'Pokemon TCG Live - Online' && <div className="bg-orange-600 text-white text-[10px] px-3 py-1 rounded-full font-black uppercase">Table {m.table || i + 1}</div>}
                                        <div className="flex-1 font-black text-lg text-center md:text-left">
                                            {m.p1Name}
                                            {/* REQUIRED FEATURE 87 & 88: Show Live Handle in Pairings List */}
                                            {t.type.includes('Live') && m.p1User ? ` (${m.p1User})` : ''}
                                        </div>
                                        <div className="font-black text-orange-600 italic px-2 bg-white rounded shadow-inner">VS</div>
                                        <div className="flex-1 font-black text-lg text-center md:text-right">
                                            {m.p2Name}
                                            {/* REQUIRED FEATURE 87 & 88: Show Live Handle in Pairings List */}
                                            {t.type.includes('Live') && m.p2User ? ` (${m.p2User})` : ''}
                                        </div>
                                        {isAdmin && m.p2 !== 'BYE' && (
                                            <div className="flex gap-1 bg-white p-1 rounded-lg border shadow-sm">
                                                <button onClick={() => { 
                                                    const rounds = { ...t.rounds }; 
                                                    rounds[`round${t.currentRound}`][i].result = 'p1'; 
                                                    rounds[`round${t.currentRound}`][i].confirmStatus = 'confirmed'; 
                                                    const chat = rounds[`round${t.currentRound}`][i].chat || [];
                                                    chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `Judge Update: P1 Win`, timestamp: Date.now(), isSystem: true });
                                                    rounds[`round${t.currentRound}`][i].chat = chat;
                                                    updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds }); 
                                                }} className={`px-3 py-1 rounded font-bold text-[10px] uppercase ${m.result === 'p1' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}>P1</button>
                                                <button onClick={() => { 
                                                    const rounds = { ...t.rounds }; 
                                                    rounds[`round${t.currentRound}`][i].result = 'draw'; 
                                                    rounds[`round${t.currentRound}`][i].confirmStatus = 'confirmed'; 
                                                    const chat = rounds[`round${t.currentRound}`][i].chat || [];
                                                    chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `Judge Update: Draw`, timestamp: Date.now(), isSystem: true });
                                                    rounds[`round${t.currentRound}`][i].chat = chat;
                                                    updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds }); 
                                                }} className={`px-3 py-1 rounded font-bold text-[10px] uppercase ${m.result === 'draw' ? 'bg-orange-600 text-white' : 'bg-gray-100'}`}>Draw</button>
                                                <button onClick={() => { 
                                                    const rounds = { ...t.rounds }; 
                                                    rounds[`round${t.currentRound}`][i].result = 'p2'; 
                                                    rounds[`round${t.currentRound}`][i].confirmStatus = 'confirmed'; 
                                                    const chat = rounds[`round${t.currentRound}`][i].chat || [];
                                                    chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `Judge Update: P2 Win`, timestamp: Date.now(), isSystem: true });
                                                    rounds[`round${t.currentRound}`][i].chat = chat;
                                                    updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds }); 
                                                }} className={`px-3 py-1 rounded font-bold text-[10px] uppercase ${m.result === 'p2' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}>P2</button>
                                                <button onClick={() => { 
                                                    const rounds = { ...t.rounds }; 
                                                    rounds[`round${t.currentRound}`][i].result = 'doubleLoss'; 
                                                    rounds[`round${t.currentRound}`][i].confirmStatus = 'confirmed'; 
                                                    const chat = rounds[`round${t.currentRound}`][i].chat || [];
                                                    chat.push({ senderId: 'SYSTEM', senderName: 'SYSTEM', text: `Judge Update: Double Loss`, timestamp: Date.now(), isSystem: true });
                                                    rounds[`round${t.currentRound}`][i].chat = chat;
                                                    updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, t.id), { rounds }); 
                                                }} className={`px-3 py-1 rounded font-bold text-[10px] uppercase ${m.result === 'doubleLoss' ? 'bg-red-800 text-white' : 'bg-gray-100 text-red-800'}`}>D. Loss</button>
                                            </div>
                                        )}
                                    </div>
                                    {isAdmin && (
                                        <div className="flex justify-center items-center gap-2 border-t pt-2">
                                            <button onClick={() => setAdminChatView(adminChatView === i ? null : i)} className={`text-[10px] font-black uppercase px-4 py-1 rounded-full border ${m.judgeCallActive ? 'bg-red-600 text-white animate-pulse' : 'bg-white text-gray-500'}`}>
                                                {m.judgeCallActive ? '⚠ JUDGE CALLED' : 'Chat / Logs'}
                                            </button>
                                            <button onClick={() => toggleChatLock(i)} className="text-xl px-2" title={m.chatLocked ? "Unlock Chat" : "Lock Chat"}>
                                                {m.chatLocked ? '🔒' : '🔓'}
                                            </button>
                                        </div>
                                    )}
                                    {isAdmin && adminChatView === i && (
                                        <MatchChat 
                                            match={m} 
                                            onSend={(txt) => updateChat(i, txt)} 
                                            onClearJudge={() => clearJudgeCall(i)}
                                            isJudge={true}
                                            userId={currentUser.uid}
                                        />
                                    )}
                                </div>
                    ))}</div>
                )}
                {activeTab === 'STANDINGS' && (
                    <div className="overflow-x-auto mt-6">
                        <table className="w-full text-left text-sm font-sans">
                            <thead className="border-b font-black uppercase text-gray-400 tracking-widest text-[10px]"><tr><th className="py-4 pl-2">Rank</th><th>Player</th><th>Points</th><th>OWP</th><th>Record</th></tr></thead>
                            <tbody>{standings.map((s, i) => (<tr key={s.userId} className={`border-b ${s.dq ? 'bg-red-50 text-red-400' : 'hover:bg-gray-50'}`}>
                                <td className="py-4 pl-2 font-black text-lg">{s.dq ? 'DQ' : i+1}</td>
                                <td className="font-bold text-gray-700">
                                    {s.name}
                                    {/* REQUIRED FEATURE 88: Show Live Handle in Standings */}
                                    {t.type.includes('Live') && s.ptcgLiveUsername ? ` (${s.ptcgLiveUsername})` : ''}
                                    {s.dropped && <span className="text-[10px] font-black text-gray-400 ml-1">(Dropped)</span>}
                                </td>
                                <td className="font-black text-orange-600 text-xl">{s.points}</td>
                                <td className="text-xs font-semibold text-gray-400">{(s.owp*100).toFixed(1)}%</td>
                                <td className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{s.wins}-{s.losses}-{s.draws}</td>
                            </tr>))}</tbody>
                        </table>
                    </div>
                )}
            </div>
            {deckView && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[2rem] w-full max-w-2xl p-8 relative max-h-[85vh] overflow-y-auto font-sans shadow-2xl border-t-[16px] border-orange-600">
                        <button onClick={() => setDeckView(null)} className="absolute top-4 right-4 text-3xl font-black text-gray-300 transition hover:rotate-90">×</button>
                        <h4 className="text-2xl font-black uppercase mb-4">{deckView.name}'s Decklist</h4>
                        <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-6 rounded-2xl border leading-relaxed text-gray-600">{deckView.decklist || "No decklist submitted."}</pre>
                    </div>
                </div>
            )}
        </div>
    );
};

const AddEditTournamentModal = ({ navigateTo, tournamentId = null }) => {
    // REQUIRED FEATURE 77: Default date to current date
    const [form, setForm] = useState({ name: '', type: 'Pokemon - Standard', date: new Date().toISOString().split('T')[0], hour: '12', min: '00', ampm: 'PM', location: '', maxParticipants: 32, decklistRequired: false, roundTimeLimit: 30, matchFormat: 'Best of 1', structure: 'Swiss', status: 'registrationOpen', description: '' });
    useEffect(() => { if (tournamentId) { getDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, tournamentId)).then(snap => { if (snap.exists()) { const d = snap.data(); const [fh, m] = (d.time || '12:00').split(':'); const h = parseInt(fh); setForm({...d, hour: (h % 12 || 12).toString(), min: m, ampm: h >= 12 ? 'PM' : 'AM'}); } }); } }, [tournamentId]);
    const save = async (e) => { 
        e.preventDefault(); 
        let h = parseInt(form.hour); if (form.ampm === 'PM' && h < 12) h += 12; if (form.ampm === 'AM' && h === 12) h = 0;
        const time = `${h.toString().padStart(2, '0')}:${form.min}`;
        const data = { ...form, time, updatedAt: new Date().toISOString() }; 
        if (tournamentId) await updateDoc(doc(db, `artifacts/${appId}/public/data/tournaments`, tournamentId), data); 
        else await addDoc(collection(db, `artifacts/${appId}/public/data/tournaments`), { ...data, players: [], rounds: {}, currentRound: 0, createdAt: new Date().toISOString() }); 
        navigateTo('tournamentList'); 
    };
    return (
        <div className="max-w-2xl mx-auto bg-white p-10 rounded-[3rem] shadow-2xl border font-sans mt-6">
            <h2 className="text-3xl font-black mb-8 uppercase tracking-tighter text-gray-900 border-b-8 border-gray-50 pb-4">{tournamentId ? 'Edit Event' : 'Create Event'}</h2>
            <form onSubmit={save} className="space-y-4">
                <input className="w-full p-4 bg-gray-50 border-none rounded-2xl font-bold text-lg" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Title" required />
                <div className="grid grid-cols-2 gap-4">
                    <select className="p-4 bg-gray-50 border-none rounded-2xl font-bold" value={form.type} onChange={e => setForm({...form, type: e.target.value})}><option>Pokemon - Standard</option><option>Pokemon - GLC</option><option>Pokemon TCG - Other</option><option>Pokemon TCG Live - Online</option><option>Magic The Gathering - Commander</option><option>One Piece Card Game</option></select>
                    <div className="flex gap-1 bg-gray-50 p-1 rounded-2xl"><select className="flex-1 p-3 bg-white rounded-xl font-bold" value={form.hour} onChange={e => setForm({...form, hour: e.target.value})}>{[1,2,3,4,5,6,7,8,9,10,11,12].map(h => <option key={h} value={h}>{h}</option>)}</select><select className="flex-1 p-3 bg-white rounded-xl font-bold" value={form.min} onChange={e => setForm({...form, min: e.target.value})}>{['00','15','30','45'].map(m => <option key={m} value={m}>{m}</option>)}</select><select className="p-3 bg-white rounded-xl font-bold" value={form.ampm} onChange={e => setForm({...form, ampm: e.target.value})}><option>AM</option><option>PM</option></select></div>
                </div>
                <div className="grid grid-cols-2 gap-4"><input type="date" className="w-full p-4 bg-gray-50 border-none rounded-2xl font-bold" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required /><input type="number" className="w-full p-4 bg-gray-50 border-none rounded-2xl font-bold" value={form.maxParticipants} onChange={e => setForm({...form, maxParticipants: parseInt(e.target.value)})} placeholder="Cap" /></div>
                {form.type !== 'Pokemon TCG Live - Online' && <input className="w-full p-4 bg-gray-50 border-none rounded-2xl font-bold" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Location" required />}
                <textarea className="w-full p-4 bg-gray-50 border-none rounded-2xl font-bold h-24" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description" />
                <div className="grid grid-cols-2 gap-4"><select className="p-4 bg-gray-50 rounded-2xl font-bold" value={form.structure} onChange={e => setForm({...form, structure: e.target.value})}><option>Swiss</option><option>Swiss with Top Cut</option></select><select className="p-4 bg-gray-50 rounded-2xl font-bold" value={form.matchFormat} onChange={e => setForm({...form, matchFormat: e.target.value})}><option>Best of 1</option><option>Best of 3</option></select></div>
                <div className="grid grid-cols-2 gap-4 items-center"><input type="number" className="w-full p-4 bg-gray-50 rounded-2xl font-bold" value={form.roundTimeLimit} onChange={e => setForm({...form, roundTimeLimit: parseInt(e.target.value)})} placeholder="Mins/Rd" /><label className="flex items-center space-x-2 bg-gray-100 p-5 rounded-2xl cursor-pointer"><input type="checkbox" className="w-6 h-6 rounded text-orange-600" checked={form.decklistRequired} onChange={e => setForm({...form, decklistRequired: e.target.checked})} /><span className="font-black uppercase text-[10px] tracking-widest">Require Decklist</span></label></div>
                <button type="submit" className="w-full py-6 bg-orange-600 text-white font-black rounded-3xl shadow-2xl uppercase tracking-widest hover:bg-orange-700 transition">Save Event</button></form></div>
    );
};

const EditProfilePage = ({ setProfile, navigateTo }) => {
    const { currentUser, pokemonPlayerProfile } = useContext(FirebaseContext);
    const save = async (e) => {
        e.preventDefault();
        const d = { name: e.target.n.value, playerIdentifier: e.target.p.value, birthYear: e.target.b.value, ptcgLiveUsername: e.target.l.value, userId: currentUser.uid, email: currentUser.email };
        await setDoc(doc(db, `artifacts/${appId}/public/data/playerProfiles`, currentUser.uid), d, { merge: true });
        setProfile(d); navigateTo('tournamentList');
    };
    return (
        <div className="max-w-md mx-auto bg-white p-12 rounded-[3rem] shadow-2xl border font-sans">
            <h2 className="text-4xl font-black uppercase mb-8 tracking-tighter text-gray-900 border-b-8 border-gray-50 pb-4">Edit Profile</h2>
            <form onSubmit={save} className="space-y-6">
                <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">Name</label>
                    <input name="n" defaultValue={pokemonPlayerProfile?.name} className="w-full p-4 bg-gray-50 rounded-2xl font-bold" placeholder="Name" required />
                </div>
                
                {/* REQUIRED FEATURE 85: Grouped Box for PID and Birth Year */}
                <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
                    <p className="text-[10px] font-black uppercase text-gray-400 mb-4 tracking-widest border-b pb-2">Required for Pokemon TCG</p>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">Player Identifier (PID)</label>
                            <input name="p" defaultValue={pokemonPlayerProfile?.playerIdentifier} className="w-full p-4 bg-white rounded-2xl font-bold shadow-sm" placeholder="PID" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">Birth Year</label>
                            <input name="b" type="number" defaultValue={pokemonPlayerProfile?.birthYear} className="w-full p-4 bg-white rounded-2xl font-bold shadow-sm" placeholder="Birth Year" />
                        </div>
                    </div>
                </div>

                {/* REQUIRED FEATURE 85: Grouped Box for PTCG Live */}
                <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
                    <p className="text-[10px] font-black uppercase text-gray-400 mb-4 tracking-widest border-b pb-2">Required For PTCG Live Events</p>
                    <div>
                        <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">PTCG Live Handle</label>
                        <input name="l" defaultValue={pokemonPlayerProfile?.ptcgLiveUsername} className="w-full p-4 bg-white rounded-2xl font-bold shadow-sm" placeholder="PTCG Live Handle" />
                    </div>
                </div>

                <button className="w-full py-5 bg-orange-600 text-white font-black rounded-[2rem] shadow-2xl uppercase tracking-widest mt-4 hover:bg-orange-700 transition">Save Profile</button>
            </form>
        </div>
    );
};

const App = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [pokemonPlayerProfile, setPokemonPlayerProfile] = useState(null);
    const [view, setView] = useState('tournamentList'); 
    const [selectedId, setSelectedId] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [showMenu, setShowMenu] = useState(false);
    const [serverOffset, setServerOffset] = useState(0);

    const isAdmin = currentUser && adminUserIds.includes(currentUser.uid);
    const isProfileComplete = (p) => p && p.playerIdentifier && p.birthYear;
    const navigateTo = (v, id = null) => { setView(v); setSelectedId(id); setShowMenu(false); };

    useEffect(() => {
        const syncServerTime = async (uid) => {
            const syncDoc = doc(db, "artifacts", appId, "sync_log", uid);
            await setDoc(syncDoc, { ts: serverTimestamp() });
            const snap = await getDoc(syncDoc);
            if (snap.exists() && snap.data().ts) {
                const serverTs = snap.data().ts.toMillis();
                const localTs = Date.now();
                setServerOffset(serverTs - localTs);
            }
        };

        const params = new URLSearchParams(window.location.search);
        const tParam = params.get('t');
        if (tParam) {
            setSelectedId(tParam);
            setView('tournamentDetails');
        }

        if (isSignInWithEmailLink(auth, window.location.href)) {
            const email = window.localStorage.getItem('emailForSignIn') || window.prompt("Confirm sign-in email:");
            signInWithEmailLink(auth, email, window.location.href).then(async (res) => {
                const pend = JSON.parse(window.localStorage.getItem('pendingRegistration') || '{}');
                if (pend.name) await setDoc(doc(db, `artifacts/${appId}/public/data/playerProfiles`, res.user.uid), { userId: res.user.uid, name: pend.name, email, createdAt: new Date().toISOString() }, { merge: true });
                window.localStorage.removeItem('emailForSignIn'); window.localStorage.removeItem('pendingRegistration');
                window.history.replaceState({}, '', '/');
            });
        }
        
        onSnapshot(collection(db, `artifacts/${appId}/public/data/tournaments`), (snap) => setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user && !user.isAnonymous) {
                await syncServerTime(user.uid);
                const snap = await getDoc(doc(db, `artifacts/${appId}/public/data/playerProfiles`, user.uid));
                if (snap.exists()) {
                    const prof = snap.data(); setPokemonPlayerProfile(prof);
                    if (!isProfileComplete(prof)) setView('editProfile');
                } else setView('editProfile');
            }
            setAuthReady(true);
        });

        return () => unsubscribe();
    }, []);

    if (!authReady) return <div className="min-h-screen flex items-center justify-center font-black text-orange-600 text-3xl uppercase tracking-tighter shadow-sm">Initializing...</div>;

    const handleLogout = async () => { await signOut(auth); setPokemonPlayerProfile(null); navigateTo('tournamentList'); };

    return (
        <FirebaseContext.Provider value={{ currentUser, pokemonPlayerProfile, isAdmin, isProfileComplete, setPokemonPlayerProfile, serverOffset }}>
            <div className="min-h-screen bg-[#FDFDFD] flex flex-col font-sans text-gray-900 overflow-x-hidden">
                <header className="bg-orange-600 shadow-md p-4 flex items-center justify-between sticky top-0 z-50 text-white">
                    <h1 className="text-3xl font-extrabold cursor-pointer tracking-tight" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.9)' }} onClick={() => navigateTo('tournamentList')}>Professor Maclid</h1>
                    <div className="flex items-center space-x-2 relative">
                        {isAdmin && <button onClick={() => navigateTo('adminUsers')} className="bg-white text-orange-600 px-3 py-1.5 rounded-lg font-black text-[10px] uppercase border shadow-sm">Admin Users</button>}
                        {currentUser && pokemonPlayerProfile ? (
                            <div className="relative">
                                <button onClick={() => setShowMenu(!showMenu)} className="bg-orange-700 px-4 py-2 rounded-lg font-bold shadow uppercase transition border border-orange-500 hover:bg-orange-800 active:scale-95">{pokemonPlayerProfile.name}</button>
                                {showMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white text-gray-900 shadow-2xl border rounded-xl py-2 z-50 overflow-hidden">
                                        <button onClick={() => navigateTo('editProfile')} className="w-full text-left px-4 py-2 text-sm font-bold uppercase hover:bg-gray-100">Edit Profile</button>
                                        <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 font-bold hover:bg-red-50">Logout</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button onClick={() => navigateTo('auth')} className="bg-white text-orange-600 font-bold px-5 py-2 rounded-lg shadow uppercase text-xs hover:bg-gray-50 transition">Login / Register</button>
                        )}
                    </div>
                </header>
                <main className="flex-1 p-6 md:p-12 max-w-7xl mx-auto w-full">
                    {view === 'tournamentList' && <TournamentList tournaments={tournaments} navigateTo={navigateTo} isAdmin={isAdmin} currentUser={currentUser} />}
                    {view === 'auth' && <Authentication />}
                    {view === 'tournamentDetails' && <TournamentDetails tournamentId={selectedId} navigateTo={navigateTo} />}
                    {view === 'createTournament' && <AddEditTournamentModal navigateTo={navigateTo} />}
                    {view === 'editTournament' && <AddEditTournamentModal navigateTo={navigateTo} tournamentId={selectedId} />}
                    {view === 'adminUsers' && <AdminUsersPanel />}
                    {view === 'editProfile' && <EditProfilePage setProfile={setPokemonPlayerProfile} navigateTo={navigateTo} />}
                </main>
                <footer className="bg-gray-800 text-gray-400 p-8 text-center text-sm border-t border-gray-700 mt-auto font-sans">
                    <p className="mb-1 font-bold text-[10px] uppercase tracking-widest">Copyright @ {new Date().getFullYear()}- Professor Maclid</p>
                </footer>
            </div>
        </FirebaseContext.Provider>
    );
};

export default App;